import RequestBuilder, { AggregationIntervals } from 'Resources/RequestBuilder';
import AgreementAttributes from 'App/babel/attributes/AgreementAttributes';
import ReportResource, { ReportEntity } from 'App/resources/Report';
import OrderAttributes from 'App/babel/attributes/Order';
import ComparisonTypes from 'Resources/ComparisonTypes';
import Agreement from 'App/resources/Model/Agreement';
import { getInitials } from 'App/helpers/getInitials';
import AppService from 'App/legacy/AppService';
import T from 'Components/Helpers/translate';
import moment from 'moment';

export type ContactSalesTotals = {
	sales: number;
	contributionMargin: number;
};

type OrderSumAggregation = {
	sum_valueInRoleCurrency?: { value: number | null };
	sum_contributionMarginInRoleCurrency?: { value: number | null };
};

/**
 * The contact's own sales for the past 12 months.
 *
 * Deliberately a copy of the filter shape in `getNOOrder`
 * (ui/app/upsales/domain/contact/controllers/contact.js) so the React card shows the same
 * figures the Angular card did: won orders (probability 100) on this contact, excluding
 * excluded stages, from the last year, summed in the viewing role's currency.
 *
 * Everything here is scoped by `contact.id` - never by the contact's company - because the
 * numbers are presented as this person's sales.
 */
export const getContactSalesTotals = async (
	contactId: number,
	excludedStageIds: number[]
): Promise<ContactSalesTotals> => {
	const rb = new RequestBuilder();
	rb.addFilter(OrderAttributes.contact, ComparisonTypes.Equals, contactId);
	rb.addFilter(OrderAttributes.probability, ComparisonTypes.Equals, 100);
	if (excludedStageIds.length) {
		rb.addFilter(OrderAttributes.stage, ComparisonTypes.NotEquals, excludedStageIds);
	}
	rb.addFilter(OrderAttributes.date, ComparisonTypes.GreaterThan, moment().subtract(1, 'year').toDate());
	rb.limit = 0;

	const valueAgg = rb.aggregationBuilder();
	valueAgg.addAggregation(rb.aggregationTypes.Sum, OrderAttributes.valueInRoleCurrency.field);
	valueAgg.done();

	const contributionMarginAgg = rb.aggregationBuilder();
	contributionMarginAgg.addAggregation(
		rb.aggregationTypes.Sum,
		OrderAttributes.contributionMarginInRoleCurrency.field
	);
	contributionMarginAgg.done();

	const res = await ReportResource.find<OrderSumAggregation>(ReportEntity.ORDER, rb.build());

	return {
		sales: res.data?.sum_valueInRoleCurrency?.value ?? 0,
		contributionMargin: res.data?.sum_contributionMarginInRoleCurrency?.value ?? 0
	};
};

export type ContactSalesGrouping = 'year' | 'month' | 'user' | 'stage';

export type ContactSalesChartColumn = {
	/** The x-axis label, e.g. `2026`, `Mar '25`, `Jane Doe`. */
	label: string;
	/** What the label collapses to on a narrow card - Angular's `compact` branch. */
	compactLabel: string;
	value: number;
	contributionMargin: number;
};

/**
 * The Angular contact chart was bounded for every grouping - `_.range(year - 11, year + 1)` for
 * years, twelve fixed months, and a slice for users and stages (contactOrder.js `createSeries`).
 * A fixed window is also why a contact with a single year of sales still gets a full row of empty
 * years rather than one lonely bar.
 */
const CHART_COLUMNS = 12;

type GroupingBucket = {
	key: string | number;
	valueSum?: { value: number | null };
	cmSum?: { value: number | null };
};

type ContactSalesChartAggregation = {
	grouping?: { buckets?: GroupingBucket[] };
};

const emptyColumn = (label: string, compactLabel: string): ContactSalesChartColumn => ({
	label,
	compactLabel,
	value: 0,
	contributionMargin: 0
});

/** The fixed twelve-period window, newest first, keyed the way the date buckets come back. */
const periodColumns = (grouping: 'year' | 'month'): { key: string; column: ContactSalesChartColumn }[] => {
	const keyFormat = grouping === 'year' ? 'YYYY' : 'YYYY-MM';
	const currentYear = moment().format('YYYY');

	return Array.from({ length: CHART_COLUMNS }, (_unused, periodsAgo) => {
		const date = moment().subtract(periodsAgo, grouping).startOf(grouping);
		const label =
			grouping === 'year' ? date.format('YYYY') : date.format(date.format('YYYY') === currentYear ? 'MMM' : "MMM 'YY"); // prettier-ignore

		return {
			key: date.format(keyFormat),
			column: emptyColumn(label, grouping === 'year' ? date.format("'YY") : label)
		};
	});
};

const namedColumn = (grouping: 'user' | 'stage', id: GroupingBucket['key']): ContactSalesChartColumn => {
	if (grouping === 'stage') {
		return emptyColumn(AppService.getStages('won').find(stage => stage.id === id)?.name ?? '', '');
	}
	const user = AppService.getUsers('order').find(u => u.id === id);
	const name = user?.name ?? T('default.inactiveUser');
	return emptyColumn(name, getInitials({ name }));
};

/**
 * The bars on the contact card's sales chart - this contact's own won orders, nothing else.
 *
 * A straight port of `createSeries` in contactOrder.js: the same `contact.id` + `probability = 100`
 * + excluded-stage filter as the summary figures and the orders list on the same screen, aggregated
 * over the same report endpoint. Deliberately NOT the Reportcenter widget the client card uses -
 * that answers a company-wide `sales_margin_quota` question, and its contact filter does not
 * survive into the query, so it plots every order in the database (WEB-8778).
 */
export const getContactSalesChartData = async (
	contactId: number,
	excludedStageIds: number[],
	grouping: ContactSalesGrouping
): Promise<ContactSalesChartColumn[]> => {
	const rb = new RequestBuilder();
	rb.addFilter(OrderAttributes.contact, ComparisonTypes.Equals, contactId);
	rb.addFilter(OrderAttributes.probability, ComparisonTypes.Equals, 100);
	if (excludedStageIds.length) {
		rb.addFilter(OrderAttributes.stage, ComparisonTypes.NotEquals, excludedStageIds);
	}
	rb.limit = 0;

	const groupingAgg = rb.aggregationBuilder();
	if (grouping === 'year' || grouping === 'month') {
		groupingAgg.addAggregation(rb.aggregationTypes.Terms, OrderAttributes.date.field);
		groupingAgg.aggregationInterval(grouping === 'year' ? AggregationIntervals.YEAR : AggregationIntervals.MONTH);
	} else {
		groupingAgg.addAggregation(
			rb.aggregationTypes.Terms,
			grouping === 'user' ? OrderAttributes.user.field : OrderAttributes.stage.field
		);
	}
	groupingAgg.aggregationName('grouping');
	groupingAgg.aggregationOrder('valueSum', false);

	const valueAgg = groupingAgg.aggregationBuilder();
	valueAgg.addAggregation(rb.aggregationTypes.Sum, OrderAttributes.valueInRoleCurrency.field);
	valueAgg.aggregationName('valueSum');
	valueAgg.done();

	const contributionMarginAgg = groupingAgg.aggregationBuilder();
	contributionMarginAgg.addAggregation(
		rb.aggregationTypes.Sum,
		OrderAttributes.contributionMarginInRoleCurrency.field
	);
	contributionMarginAgg.aggregationName('cmSum');
	contributionMarginAgg.done();
	groupingAgg.done();

	const res = await ReportResource.find<ContactSalesChartAggregation>(ReportEntity.ORDER, rb.build());
	const buckets = res.data?.grouping?.buckets ?? [];

	const fill = (column: ContactSalesChartColumn, bucket: GroupingBucket) => ({
		...column,
		value: bucket.valueSum?.value ?? 0,
		contributionMargin: bucket.cmSum?.value ?? 0
	});

	if (grouping === 'user' || grouping === 'stage') {
		// These buckets arrive sorted by value, so the head of the list is the part worth plotting.
		return buckets.slice(0, CHART_COLUMNS).map(bucket => fill(namedColumn(grouping, bucket.key), bucket));
	}

	const window = periodColumns(grouping);
	const bucketByKey = new Map(
		buckets.map(bucket => [moment(bucket.key).format(grouping === 'year' ? 'YYYY' : 'YYYY-MM'), bucket])
	);
	// Periods outside the window are dropped rather than plotted, exactly as Angular's
	// `categories.indexOf(...) !== -1` guard did.
	return window.map(({ key, column }) => {
		const bucket = bucketByKey.get(key);
		return bucket ? fill(column, bucket) : column;
	});
};

export type NextSubscription = {
	date: Date;
	value: number;
};

/**
 * The contact's next subscription order - the "NEXT SUBSCRIPTION x in y months" figure on the
 * Angular card. Mirrors `getAgreements` in
 * ui/app/upsales/domain/contact/controllers/contactOrder.js: the contact's active subscriptions
 * (no end date, or one in the future), then the nearest next order date, preferring the larger
 * value when two fall on the same day.
 */
export const getNextContactSubscription = async (contactId: number): Promise<NextSubscription | null> => {
	const rb = new RequestBuilder();
	rb.addFilter(AgreementAttributes.contact, ComparisonTypes.Equals, contactId);
	const orBuilder = rb.orBuilder();
	orBuilder.next();
	orBuilder.addFilter(AgreementAttributes.endDate, ComparisonTypes.Equals, null);
	orBuilder.next();
	orBuilder.addFilter(AgreementAttributes.endDate, ComparisonTypes.GreaterThan, new Date());
	orBuilder.done();

	const res: { data: Agreement[] } = await Tools.Agreement.customer(AppService.getCustomerId()).find(rb.build());

	const defaultCurrency = AppService.getMetadata().defaultCurrency;
	return (res.data ?? []).reduce<NextSubscription | null>((next, agreement) => {
		const date = agreement.metadata?.agreementNextOrderDate;
		if (!date) {
			return next;
		}
		const value =
			(agreement.currency || '').toLowerCase() === defaultCurrency.iso.toLowerCase()
				? agreement.value
				: Math.round(agreement.valueInMasterCurrency * defaultCurrency.rate);
		if (!next || moment(date).isBefore(next.date, 'day')) {
			return { date, value };
		}
		if (moment(date).isSame(next.date, 'day') && value > next.value) {
			return { date, value };
		}
		return next;
	}, null);
};
